'use client';

import { CompanyFormData } from '@/shared/lib/validation/registration-wizard/company';
import { SignupCredentialsFormData } from '@/shared/lib/validation/registration-wizard/credentials';
import { ProfileFormData } from '@/shared/lib/validation/registration-wizard/profile';
import { useRef } from 'react';
import { ArrowRightIcon, WarningIcon } from '@phosphor-icons/react/dist/ssr';
import Link from 'next/link';
import { Button, Separator, Text } from '@/shared/ui/core';
import type { FormHandle } from '@/shared/ui/forms/form-handle';
import { RequiredDataNote } from '@/shared/ui/kit/required-data-note';
import { HaveAnAccount } from '@/shared/ui/kit/have-an-account';
import {
    RegistrationCard, RegistrationCardContent, RegistrationCardDescription, RegistrationCardHeader,
    RegistrationCardTitle,
} from '@/shared/ui/layout/registration-card';
import {
    FORGOT_PATHNAME,
    LOGIN_PATHNAME,
    REGISTRATION_SUCCESS_PATHNAME,
} from '@/shared/constants/pathnames';
import { useSplitWizard } from '../../../model/split-wizard-context';
import { useSplitWizardSubmit } from '../../../model/split-wizard-submit-context';
import { ProfileSubForm } from './profile-sub-form';
import { CompanySubForm } from './company-sub-form';
import { CredentialsSubForm } from './credentials-sub-form';
import { WizardFooterSplit } from '../../wizard-footer-split';
import styles from './step-profile.module.scss';


export const StepProfileSplit = () => {
    const { state, dispatch } = useSplitWizard();

    const { submit: signup, loading: signupLoading, error: signupError, setError } = useSplitWizardSubmit();

    const profileRef     = useRef<FormHandle<ProfileFormData>>(null);
    const companyRef     = useRef<FormHandle<CompanyFormData>>(null);
    const credentialsRef = useRef<FormHandle<SignupCredentialsFormData>>(null);

    const handleNext = async () => {
        setError(null);

        const [ profileData, companyData, credentialsData ] = await Promise.all([
            profileRef.current?.submit(),
            companyRef.current?.submit(),
            credentialsRef.current?.submit(),
        ]);

        if (!profileData || !companyData || !credentialsData) return;

        const result = await signup({
            ...state.data,
            profile    : profileData,
            company    : companyData,
            credentials: credentialsData,
        });
        if (!result) return;

        const email = credentialsData.email || state.data.email?.email || '';
        window.location.href = `${ REGISTRATION_SUCCESS_PATHNAME }?email=${ encodeURIComponent(email) }`;
    };

    return (
        <div data-template="step-profile">
            <RegistrationCard data-template="registration-steps-card">
                <RegistrationCardHeader data-template="registration-steps-card-header">
                    <RegistrationCardTitle data-template="registration-steps-card-title">
                        Profile & basic info
                    </RegistrationCardTitle>

                    <RegistrationCardDescription data-template="registration-steps-card-description">
                        Enter your details for prompt registration.
                    </RegistrationCardDescription>
                </RegistrationCardHeader>

                <RegistrationCardContent data-template="registration-steps-card-content">
                    <div className={ styles.root }>
                        <div className={ styles.profile_grid }>
                            <Text
                                as="h3"
                                weight="semibold"
                                lineHeight={ 1.2 }
                                className={ styles.header_slot }
                            >
                                User profile
                            </Text>

                            <ProfileSubForm
                                ref={ profileRef }
                                defaultValues={ state.data.profile }
                                onSubmit={ (data) => dispatch({ type: 'commit', key: 'profile', value: data }) }
                            />
                        </div>

                        <div className={ styles.company_grid }>
                            <Text
                                as="h3"
                                weight="semibold"
                                lineHeight={ 1.2 }
                                className={ styles.header_slot }
                            >
                                Company information
                            </Text>

                            <CompanySubForm
                                ref={ companyRef }
                                defaultValues={ state.data.company }
                                onSubmit={ (data) => dispatch({ type: 'commit', key: 'company', value: data }) }
                            />
                        </div>

                        <Separator
                            orientation="vertical"
                            className={ styles.separator }
                        />

                        <div className={ styles.credentials_grid }>
                            <Text
                                as="h3"
                                weight="semibold"
                                lineHeight={ 1.2 }
                                className={ styles.header_slot }
                            >
                                Login credentials
                            </Text>

                            <CredentialsSubForm
                                ref={ credentialsRef }
                                defaultValues={ state.data.credentials ?? { email: state.data.email?.email ?? '' } }
                                onSubmit={ (data) => dispatch({ type: 'commit', key: 'credentials', value: data }) }
                            />
                        </div>

                        {
                            signupError?.kind === 'email_exists' &&
                            <div className={ styles.exists_warning }>
                                <WarningIcon
                                    size={ 20 }
                                    weight="fill"
                                    className={ styles.warning_icon }
                                />
                                <Text size="md" tone="warning">
                                    Looks like you already have an account. Try to <Link href={ `${ LOGIN_PATHNAME }?email=${ encodeURIComponent(state.data.email?.email ?? '') }` } className={ styles.link }>log in</Link> or <Link href={ FORGOT_PATHNAME } className={ styles.link }>reset your password</Link>.
                                </Text>
                            </div>
                        }

                        {
                            signupError?.kind === 'generic' &&
                            <Text
                                size="sm"
                                tone="destructive"
                                className={ styles.error }
                            >
                                { signupError.message }
                            </Text>
                        }
                    </div>
                </RegistrationCardContent>
            </RegistrationCard>

            <WizardFooterSplit
                state={ state }
                left={ <RequiredDataNote className={ styles.required }/> }
                right={
                    <Button
                        onClick={ handleNext }
                        leftIcon={ <ArrowRightIcon/> }
                        loading={ signupLoading }
                    >
                        Continue
                    </Button>
                }
                FooterAccount={
                    <HaveAnAccount/>
                }
            />
        </div>
    );
};
